import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store/redux/store/store';
import Icon from '../../../components/AppIcon';
import { Network } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import { getHierarchy, EmployeeHierarchyView } from '../../../api/Employee/organizationalStructure';
import { formatDate } from '../../../utils/dateFormatter';
import { staffNumberOf } from '../../../utils/staffNumber';

interface OrgDataMap {
  [key: string]: EmployeeHierarchyView & {
    location?: string;
    employmentType?: string;
    managerialLevel?: string;
  };
}

interface Filters {
  department: string;
  location: string;
  employmentType: string;
  managerialLevel: string;
}

// Department colors with glow effects
const departmentColors: Record<string, { bg: string; border: string; text: string; glow: string; gradient: string }> = {
  'Executive': { bg: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', glow: 'shadow-purple-200', gradient: 'from-purple-400 to-purple-600' },
  'Engineering': { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', glow: 'shadow-blue-200', gradient: 'from-blue-400 to-blue-600' },
  'Marketing': { bg: 'bg-pink-50', border: 'border-pink-400', text: 'text-pink-700', glow: 'shadow-pink-200', gradient: 'from-pink-400 to-pink-600' },
  'Operations': { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-700', glow: 'shadow-green-200', gradient: 'from-green-400 to-green-600' },
  'HR': { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', glow: 'shadow-amber-200', gradient: 'from-amber-400 to-amber-600' },
  'Finance': { bg: 'bg-cyan-50', border: 'border-cyan-400', text: 'text-cyan-700', glow: 'shadow-cyan-200', gradient: 'from-cyan-400 to-cyan-600' },
  'Sales': { bg: 'bg-rose-50', border: 'border-rose-400', text: 'text-rose-700', glow: 'shadow-rose-200', gradient: 'from-rose-400 to-rose-600' },
  'IT': { bg: 'bg-indigo-50', border: 'border-indigo-400', text: 'text-indigo-700', glow: 'shadow-indigo-200', gradient: 'from-indigo-400 to-indigo-600' },
  'Technical': { bg: 'bg-teal-50', border: 'border-teal-400', text: 'text-teal-700', glow: 'shadow-teal-200', gradient: 'from-teal-400 to-teal-600' },
};

const defaultColors = { bg: 'bg-gray-50', border: 'border-border', text: 'text-gray-700', glow: 'shadow-gray-200', gradient: 'from-gray-400 to-gray-600' };

// Generate consistent color from string (for initials avatar)
const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#6366F1'];
  return colors[Math.abs(hash) % colors.length];
};

// Get initials from name
const getInitials = (name: string): string => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
};

const OrganizationalStructure: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState<(EmployeeHierarchyView & { location?: string; employmentType?: string; managerialLevel?: string }) | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'photo' | 'compact' | 'detailed'>('detailed');
  const [layoutMode, setLayoutMode] = useState<'hierarchy' | 'department'>('hierarchy');
  const [showLegend, setShowLegend] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    department: 'all',
    location: 'all',
    employmentType: 'all',
    managerialLevel: 'all'
  });
  const [hoveredEmployee, setHoveredEmployee] = useState<EmployeeHierarchyView | null>(null);
  const [matchedNodes, setMatchedNodes] = useState<Set<string>>(new Set());

  // Pan & Drag state - using refs for performance (no re-renders during drag)
  const [isPanning, setIsPanning] = useState(false);
  const panStateRef = useRef({
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    rafId: 0
  });
  const [, setPanOffset] = useState({ x: 0, y: 0 }); // Only setter used to trigger re-renders when pan changes

  // Keyboard navigation
  const [keyboardFocusId, setKeyboardFocusId] = useState<string | null>(null);

  const chartRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const initialFitDoneRef = useRef(false);

  // Data state
  const [orgData, setOrgData] = useState<OrgDataMap>({});

  /*
    CO-833 — who may OPEN a node's profile, mirroring AccountController.CanReadColleagueRecordAsync:
    self, Hr, Admin, Chief (org-wide), or the viewer's DIRECT reports (`IsManagerOf`, the owner's
    2026-10-05 decision). A Manager's chart is the whole subtree (GetAllSubordinates is transitive), so
    without this every skip-level node's "View Profile" led straight to the 403 no-access page. Those
    buttons are withheld, not disabled: the node card and side panel still show what the chart shows.
    `roles` is the real role list (a Manager can also hold Chief); the primary `role` is the fallback.
  */
  const viewerId = useSelector((state: RootState) => state.user.userId);
  const viewerRole = useSelector((state: RootState) => state.user.role);
  // `roles` is kept off the inferred user-state type (see userReducer `withRoles`); read it defensively.
  const viewerRoles = useSelector((state: RootState) => (state.user as { roles?: unknown }).roles);
  const viewerRealRoles: string[] = Array.isArray(viewerRoles) && viewerRoles.length > 0
    ? viewerRoles.map(String)
    : [String(viewerRole ?? '')];
  const hasOrgWideProfileRead = viewerRealRoles.some(r => r === 'Hr' || r === 'Admin' || r === 'Chief');
  const canOpenProfile = (employeeId: string): boolean =>
    hasOrgWideProfileRead
    || (viewerId != null && String(employeeId) === String(viewerId))
    || (viewerId != null && (orgData[viewerId]?.directReportIds ?? []).some(rid => String(rid) === String(employeeId)));
  const [rootEmployeeIds, setRootEmployeeIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Unique values for filters
  const [departments, setDepartments] = useState<string[]>([]);

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getHierarchy();

        if (response.status === 200 && response.payload) {
          const dataMap: OrgDataMap = {};
          const deptSet = new Set<string>();
          const rootCandidates: string[] = [];

          response.payload.forEach((emp) => {
            dataMap[emp.id] = {
              ...emp,
              location: 'Office',
              employmentType: 'Full-time',
              managerialLevel: emp.isManager ? (emp.employeeCount > 10 ? 'executive' : 'manager') : 'individual',
            };

            if (emp.department) deptSet.add(emp.department);

            // Collect all employees without a manager (root candidates)
            if (!emp.managerId) {
              rootCandidates.push(emp.id);
            }
          });

          // Sort root candidates: managers first (by employee count desc), then non-managers alphabetically
          rootCandidates.sort((a, b) => {
            const empA = dataMap[a];
            const empB = dataMap[b];
            // Managers come first
            if (empA.isManager && !empB.isManager) return -1;
            if (!empA.isManager && empB.isManager) return 1;
            // Among managers, sort by employee count (descending)
            if (empA.isManager && empB.isManager) {
              return empB.employeeCount - empA.employeeCount;
            }
            // Among non-managers, sort alphabetically by name
            return (empA.name || '').localeCompare(empB.name || '');
          });

          setOrgData(dataMap);
          setRootEmployeeIds(rootCandidates);
          setDepartments(Array.from(deptSet).sort());

          // Expand the first root (the main hierarchy tree)
          if (rootCandidates.length > 0) {
            setExpandedNodes(new Set([rootCandidates[0]]));
            setKeyboardFocusId(rootCandidates[0]);
          }
        } else {
          const errorMsg = response.errors?.length > 0
            ? response.errors[0].description
            : t('orgChart.loadError');
          setError(errorMsg);
        }
      } catch (err) {
        setError(t('orgChart.loadError'));
        console.error('Error loading hierarchy:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Get breadcrumb path for an employee
  const getBreadcrumbPath = useCallback((employeeId: string): Array<{ id: string; name: string }> => {
    const path: Array<{ id: string; name: string }> = [];
    let currentId: string | undefined = employeeId;

    while (currentId && orgData[currentId]) {
      const emp: OrgDataMap[string] = orgData[currentId];
      path.unshift({ id: currentId, name: emp.name || t('common.unknown') });
      currentId = emp.managerId;
    }

    return path;
  }, [orgData]);

  // Expand/Collapse helpers
  const expandAll = useCallback(() => {
    const allIds = new Set(Object.keys(orgData));
    setExpandedNodes(allIds);
  }, [orgData]);

  const collapseAll = useCallback(() => {
    if (rootEmployeeIds.length > 0) {
      setExpandedNodes(new Set(rootEmployeeIds));
    } else {
      setExpandedNodes(new Set());
    }
  }, [rootEmployeeIds]);

  const expandToLevel = useCallback((level: number) => {
    if (rootEmployeeIds.length === 0) return;

    const toExpand = new Set<string>();
    // Start BFS from all root employees
    const queue: Array<{ id: string; depth: number }> = rootEmployeeIds.map(id => ({ id, depth: 0 }));

    while (queue.length > 0) {
      const { id, depth } = queue.shift()!;
      if (depth < level) {
        toExpand.add(id);
        const emp = orgData[id];
        if (emp?.directReportIds) {
          emp.directReportIds.forEach(childId => {
            queue.push({ id: childId, depth: depth + 1 });
          });
        }
      }
    }

    setExpandedNodes(toExpand);
  }, [orgData, rootEmployeeIds]);

  // Filter and search logic
  const applyFilters = useCallback((employee: OrgDataMap[string]) => {
    if (filters.department !== 'all' && employee.department !== filters.department) return false;
    if (filters.location !== 'all' && employee.location !== filters.location) return false;
    if (filters.employmentType !== 'all' && employee.employmentType !== filters.employmentType) return false;
    if (filters.managerialLevel !== 'all' && employee.managerialLevel !== filters.managerialLevel) return false;
    return true;
  }, [filters]);

  const searchMatches = useCallback((employee: OrgDataMap[string]) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      employee.name?.toLowerCase().includes(term) ||
      employee.title?.toLowerCase().includes(term) ||
      employee.department?.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Update matched nodes when search changes
  useEffect(() => {
    if (searchTerm) {
      const matches = new Set<string>();
      const pathsToExpand = new Set<string>(expandedNodes);

      Object.values(orgData).forEach(employee => {
        if (searchMatches(employee)) {
          matches.add(employee.id);
          let currentId = employee.managerId;
          while (currentId) {
            pathsToExpand.add(currentId);
            currentId = orgData[currentId]?.managerId;
          }
        }
      });

      setMatchedNodes(matches);
      setExpandedNodes(pathsToExpand);
    } else {
      setMatchedNodes(new Set());
    }
  }, [searchTerm, orgData, searchMatches]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Focus search on /
      if (e.key === '/' && document.activeElement !== searchInputRef.current) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      // Escape to close side panel
      if (e.key === 'Escape') {
        if (selectedEmployee) {
          setSelectedEmployee(null);
        }
        return;
      }

      // Only handle arrow keys when not in search input
      if (document.activeElement === searchInputRef.current) return;

      const currentEmp = keyboardFocusId ? orgData[keyboardFocusId] : null;
      if (!currentEmp) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          // Go to first direct report if expanded
          if (expandedNodes.has(keyboardFocusId!) && currentEmp.directReportIds?.length) {
            setKeyboardFocusId(currentEmp.directReportIds[0]);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          // Go to manager
          if (currentEmp.managerId) {
            setKeyboardFocusId(currentEmp.managerId);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          // Go to previous sibling
          if (currentEmp.managerId) {
            const manager = orgData[currentEmp.managerId];
            const siblings = manager?.directReportIds || [];
            const currentIndex = siblings.indexOf(keyboardFocusId!);
            if (currentIndex > 0) {
              setKeyboardFocusId(siblings[currentIndex - 1]);
            }
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          // Go to next sibling
          if (currentEmp.managerId) {
            const manager = orgData[currentEmp.managerId];
            const siblings = manager?.directReportIds || [];
            const currentIndex = siblings.indexOf(keyboardFocusId!);
            if (currentIndex < siblings.length - 1) {
              setKeyboardFocusId(siblings[currentIndex + 1]);
            }
          }
          break;
        case 'Enter':
          e.preventDefault();
          // Toggle expand or select
          if (currentEmp.directReportIds?.length) {
            toggleNodeExpansion(keyboardFocusId!);
          } else {
            setSelectedEmployee(currentEmp);
          }
          break;
        case ' ':
          e.preventDefault();
          // Select employee
          setSelectedEmployee(currentEmp);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [keyboardFocusId, orgData, expandedNodes, selectedEmployee]);

  // Pan & Drag handlers - optimized with refs and RAF for smooth performance
  const updateTransform = useCallback(() => {
    if (chartRef.current) {
      const { offsetX, offsetY } = panStateRef.current;
      chartRef.current.style.transform = `scale(${zoomLevel}) translate(${offsetX / zoomLevel}px, ${offsetY / zoomLevel}px)`;
    }
  }, [zoomLevel]);

  // Sync transform when zoom changes (but not during panning)
  useEffect(() => {
    if (!isPanning) {
      updateTransform();
    }
  }, [zoomLevel, isPanning, updateTransform]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click
    e.preventDefault();
    setIsPanning(true);
    panStateRef.current.startX = e.clientX - panStateRef.current.offsetX;
    panStateRef.current.startY = e.clientY - panStateRef.current.offsetY;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;

    // Cancel any pending RAF to avoid stacking
    if (panStateRef.current.rafId) {
      cancelAnimationFrame(panStateRef.current.rafId);
    }

    // Calculate new offset
    const newOffsetX = e.clientX - panStateRef.current.startX;
    const newOffsetY = e.clientY - panStateRef.current.startY;

    // Update ref immediately (no re-render)
    panStateRef.current.offsetX = newOffsetX;
    panStateRef.current.offsetY = newOffsetY;

    // Use RAF for smooth DOM updates
    panStateRef.current.rafId = requestAnimationFrame(() => {
      updateTransform();
    });
  }, [isPanning, updateTransform]);

  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      // Sync state with ref for other features that need it
      setPanOffset({ x: panStateRef.current.offsetX, y: panStateRef.current.offsetY });
    }
  }, [isPanning]);

  const handleMouseLeave = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      setPanOffset({ x: panStateRef.current.offsetX, y: panStateRef.current.offsetY });
    }
  }, [isPanning]);

  const resetPan = useCallback(() => {
    panStateRef.current.offsetX = 0;
    panStateRef.current.offsetY = 0;
    setPanOffset({ x: 0, y: 0 });
    updateTransform();
  }, [updateTransform]);

  const toggleNodeExpansion = (nodeId: string, nodeElement?: HTMLElement | null) => {
    const newExpanded = new Set(expandedNodes);
    const isExpanding = !newExpanded.has(nodeId);

    if (isExpanding) {
      newExpanded.add(nodeId);
    } else {
      newExpanded.delete(nodeId);
    }
    setExpandedNodes(newExpanded);

    // CO-915 — keep the node where it was. The old code re-centred it with a formula that assumed a
    // top-LEFT transform origin at the container's corner, but the chart scales from 'top center' and
    // is at least as wide as the container (`min-w-full`), and expanding a node widens it: the origin
    // moves, so the "centre" landed somewhere else and the node the viewer had just clicked slid off
    // screen. Measuring the node's screen position before and after the re-render and panning by the
    // difference holds it still whatever the origin, zoom or layout does. The translate sits inside
    // the scale, so a screen shift of d is exactly an offset change of d.
    if (nodeElement && chartContainerRef.current && chartRef.current) {
      const before = nodeElement.getBoundingClientRect();
      // Two frames: the first commits the new subtree, the second has it laid out.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const container = chartContainerRef.current;
        if (!container || !nodeElement.isConnected) return;

        const after = nodeElement.getBoundingClientRect();
        let newOffsetX = panStateRef.current.offsetX + (before.left - after.left);
        let newOffsetY = panStateRef.current.offsetY + (before.top - after.top);

        // A node toggled while partly off screen (keyboard, or a drag left it at the edge) is then
        // brought fully inside the container, with a small margin, rather than held off screen.
        const box = container.getBoundingClientRect();
        const margin = 24;
        const left = before.left;
        const right = before.left + after.width;
        const top = before.top;
        const bottom = before.top + after.height;
        if (left < box.left + margin) newOffsetX += box.left + margin - left;
        else if (right > box.right - margin) newOffsetX -= right - (box.right - margin);
        if (top < box.top + margin) newOffsetY += box.top + margin - top;
        else if (bottom > box.bottom - margin) newOffsetY -= bottom - (box.bottom - margin);

        panStateRef.current.offsetX = newOffsetX;
        panStateRef.current.offsetY = newOffsetY;
        setPanOffset({ x: newOffsetX, y: newOffsetY });
        updateTransform();
      }));
    }
  };

  const handleEmployeeClick = (employee: OrgDataMap[string]) => {
    setSelectedEmployee(employee);
    setKeyboardFocusId(employee.id);
  };

  const handleZoom = (direction: 'in' | 'out' | 'reset' | 'fit') => {
    if (direction === 'in') {
      setZoomLevel(prev => Math.min(prev * 1.2, 3));
    } else if (direction === 'out') {
      setZoomLevel(prev => Math.max(prev / 1.2, 0.3));
    } else if (direction === 'reset') {
      // Reset to 100% zoom and center the content
      if (chartRef.current && chartContainerRef.current) {
        // Temporarily reset transform to measure actual content size
        chartRef.current.style.transform = 'scale(1) translate(0px, 0px)';

        requestAnimationFrame(() => {
          if (!chartRef.current || !chartContainerRef.current) return;

          const chartWidth = chartRef.current.scrollWidth;
          const chartHeight = chartRef.current.scrollHeight;
          const containerWidth = chartContainerRef.current.clientWidth;
          const containerHeight = chartContainerRef.current.clientHeight;

          // Calculate offset to center the content at 100% zoom
          const newOffsetX = (containerWidth - chartWidth) / 2;
          const newOffsetY = (containerHeight - chartHeight) / 2;

          // Apply centered position (only center if content fits, otherwise align to top-left with small offset)
          panStateRef.current.offsetX = Math.min(newOffsetX, 32);
          panStateRef.current.offsetY = Math.min(newOffsetY, 32);
          setPanOffset({ x: panStateRef.current.offsetX, y: panStateRef.current.offsetY });
          setZoomLevel(1);
        });
      } else {
        setZoomLevel(1);
        resetPan();
      }
    } else if (direction === 'fit') {
      if (chartRef.current && chartContainerRef.current) {
        // Temporarily reset zoom to measure actual content size
        chartRef.current.style.transform = 'scale(1) translate(0px, 0px)';

        // Wait for DOM to update then calculate
        requestAnimationFrame(() => {
          if (!chartRef.current || !chartContainerRef.current) return;

          const chartWidth = chartRef.current.scrollWidth;
          const chartHeight = chartRef.current.scrollHeight;
          const containerWidth = chartContainerRef.current.clientWidth;
          const containerHeight = chartContainerRef.current.clientHeight;

          // Calculate zoom to fit both dimensions with some padding
          const padding = 64;
          const zoomX = (containerWidth - padding) / chartWidth;
          const zoomY = (containerHeight - padding) / chartHeight;
          const newZoom = Math.min(zoomX, zoomY, 1); // Don't zoom in past 100%
          const finalZoom = Math.max(newZoom, 0.1);

          // Calculate offset to center the scaled content in the container
          const scaledWidth = chartWidth * finalZoom;
          const scaledHeight = chartHeight * finalZoom;
          const newOffsetX = (containerWidth - scaledWidth) / 2;
          const newOffsetY = (containerHeight - scaledHeight) / 2;

          // Apply centered position
          panStateRef.current.offsetX = newOffsetX;
          panStateRef.current.offsetY = newOffsetY;
          setPanOffset({ x: newOffsetX, y: newOffsetY });
          setZoomLevel(finalZoom);
        });
      }
    }
  };

  // First load: fit the main tree's WIDTH to the viewport and top-align it, so the root is on screen
  // instead of the chart opening at 100% scrolled to its left edge (a wide tree put the root, which
  // is centred over its reports, off to the right with only a row of reports visible). Measured with
  // bounding rects rather than layout maths, so it holds in RTL and under the top-centre origin.
  // Height is deliberately not fitted: unmanaged employees each form their own root and wrap below,
  // and squeezing all of them in would shrink the main tree past legibility. Panning reaches them.
  const MIN_INITIAL_ZOOM = 0.5;
  const fitInitialView = useCallback(() => {
    const chart = chartRef.current;
    const container = chartContainerRef.current;
    const mainRoot = chart?.querySelector<HTMLElement>('[data-org-root]');
    if (!chart || !container || !mainRoot) return;

    const previousTransition = chart.style.transition;
    chart.style.transition = 'none';

    const padding = 64;
    const zoom = Math.min(1, Math.max(MIN_INITIAL_ZOOM, (container.clientWidth - padding) / mainRoot.offsetWidth));
    chart.style.transform = `scale(${zoom}) translate(0px, 0px)`;

    const containerRect = container.getBoundingClientRect();
    const chartRect = chart.getBoundingClientRect();
    const rootRect = mainRoot.getBoundingClientRect();
    const offsetX = containerRect.left + containerRect.width / 2 - (rootRect.left + rootRect.width / 2);
    const offsetY = containerRect.top - chartRect.top;

    panStateRef.current.offsetX = offsetX;
    panStateRef.current.offsetY = offsetY;
    chart.style.transform = `scale(${zoom}) translate(${offsetX / zoom}px, ${offsetY / zoom}px)`;
    setPanOffset({ x: offsetX, y: offsetY });
    setZoomLevel(zoom);
    requestAnimationFrame(() => {
      if (chartRef.current) chartRef.current.style.transition = previousTransition;
    });
  }, []);

  useEffect(() => {
    if (initialFitDoneRef.current || loading || layoutMode !== 'hierarchy' || rootEmployeeIds.length === 0) return;
    initialFitDoneRef.current = true;
    // Two frames: the first lets React commit the tree, the second lets the browser lay it out.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(fitInitialView);
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [loading, layoutMode, rootEmployeeIds, fitInitialView]);

  const handleExport = () => {
    // The first column is the staff number, never `emp.id`: that is the internal user key, a system
    // id that platform users must not see (owner rule 2026-10-28). staffNumberOf also reads the pre-rename
    // `employeeId`, so the column stays filled against a server from either side of the deploy.
    // Cells are quoted so a comma stays in its cell, and a value that starts with = + - @ (or a tab/CR) gets a
    // leading apostrophe so a spreadsheet shows it as text instead of running it as a formula.
    const cell = (value: string | number | null | undefined) => {
      const text = String(value ?? '');
      const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
      return `"${safe.replace(/"/g, '""')}"`;
    };
    const header = [
      t('orgChart.csv.staffNumber'),
      t('orgChart.csv.name'),
      t('orgChart.csv.title'),
      t('orgChart.csv.department'),
      t('orgChart.csv.email'),
      t('orgChart.csv.manager'),
      t('orgChart.csv.directReports'),
    ];
    const rows = Object.values(orgData).map(emp => [
      staffNumberOf(emp),
      emp.name,
      emp.title,
      emp.department,
      emp.email,
      emp.managerId ? orgData[emp.managerId]?.name : t('orgChart.csv.noManager'),
      emp.directReportIds?.length || 0,
    ]);
    // BOM so spreadsheet apps read Arabic names as UTF-8; encodeURIComponent so a '#' cannot cut the file short.
    const csv = '﻿' + [header, ...rows].map(r => r.map(cell).join(',')).join('\n');

    const encodedUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "organizational_structure.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  // Render curved connector line (SVG)
  const renderConnectorLine = (isVertical: boolean = true, animate: boolean = true) => {
    if (isVertical) {
      return (
        <svg className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-4 h-8" viewBox="0 0 16 32">
          <path
            d="M8 0 Q8 16, 8 32"
            fill="none"
            stroke="#CBD5E1"
            strokeWidth="2"
            className={animate ? 'animate-draw-line' : ''}
          />
        </svg>
      );
    }
    return null;
  };

  const renderEmployeeCard = (employee: OrgDataMap[string], level = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(employee.id);
    const hasDirectReports = employee.directReportIds && employee.directReportIds.length > 0;
    const isFiltered = applyFilters(employee);
    const isMatched = matchedNodes.has(employee.id);
    const isDimmed = searchTerm && !isMatched;
    const isKeyboardFocused = keyboardFocusId === employee.id;
    const deptColors = departmentColors[employee.department || ''] || defaultColors;

    if (!isFiltered) return null;

    let cardClasses = '';
    let avatarSize = '';

    if (viewMode === 'photo') {
      cardClasses = 'min-w-[200px] max-w-[220px]';
      avatarSize = 'w-20 h-20';
    } else if (viewMode === 'compact') {
      cardClasses = 'min-w-[180px] max-w-[200px]';
      avatarSize = 'w-8 h-8';
    } else {
      cardClasses = 'min-w-[280px] max-w-[320px]';
      avatarSize = 'w-12 h-12';
    }

    const initials = getInitials(employee.name || '');
    const avatarColor = stringToColor(employee.id);

    /*
      EXIT-DATE LIFECYCLE (docs/exit-date-lifecycle-spec.md §3.6).

      People who have LEFT are not on this chart at all — the server drops them, so there is no
      "former employee" node state to design. The only thing the chart has to say is that somebody
      still on it has a last day coming, and when. A quiet inline chip under the name, in the same
      warning tint the directory and the profile use, rather than a second floating badge competing
      with the manager crown for the card's corners.

      In compact mode the card is 180px wide and the name already truncates, so the chip degrades to
      the icon alone with the date in its tooltip — losing the date is better than losing the name.
    */
    const isLeaving = employee.lifecycleState === 'Leaving' && !!employee.exitDate;
    const leavingLabel = isLeaving
      ? t('employees:lifecycle.leavingOn', { date: formatDate(new Date(employee.exitDate as string)) })
      : '';
    const LeavingChip = ({ iconOnly = false }: { iconOnly?: boolean }) => !isLeaving ? null : (
      <span
        title={leavingLabel}
        aria-label={leavingLabel}
        role="img"
        className={`inline-flex items-center gap-1 rounded-full border border-warning/20 bg-warning/10 text-warning font-medium ${iconOnly ? 'p-1' : 'px-2 py-0.5 text-[10px]'}`}
      >
        <Icon name="CalendarClock" size={11} />
        {!iconOnly && <span className="truncate">{leavingLabel}</span>}
      </span>
    );

    return (
      <div key={employee.id} className="flex flex-col items-center transition-all duration-300">
        {/* Employee Node */}
        <div className="relative group">
          <div
            className={`
              bg-card rounded-xl border-2 cursor-pointer transition-all duration-300
              ${cardClasses}
              ${deptColors.border}
              ${isMatched ? 'ring-4 ring-primary ring-opacity-50' : ''}
              ${isDimmed ? 'opacity-40' : ''}
              ${isKeyboardFocused ? 'ring-4 ring-ring ring-opacity-70' : ''}
              shadow-md
              ${viewMode === 'photo' ? 'p-4 text-center' : viewMode === 'compact' ? 'p-2' : 'p-4'}
              hover:shadow-xl hover:scale-[1.02] hover:${deptColors.glow}
              group-hover:border-opacity-100
            `}
            onClick={() => handleEmployeeClick(employee)}
            onMouseEnter={() => setHoveredEmployee(employee)}
            onMouseLeave={() => setHoveredEmployee(null)}
            tabIndex={0}
            role="button"
            aria-label={`${employee.name}, ${employee.title}`}
          >
            {/* Manager Badge */}
            {employee.isManager && viewMode !== 'compact' && (
              <div className="absolute -top-2 -end-2 w-7 h-7 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-lg transform transition-transform group-hover:scale-110">
                <Icon name="Crown" size={14} color="white" />
              </div>
            )}

            {/* Photo-centric View */}
            {viewMode === 'photo' && (
              <div className="flex flex-col items-center space-y-2">
                <div className={`${avatarSize} rounded-full flex items-center justify-center overflow-hidden shadow-md ring-2 ring-white`}
                  style={{ backgroundColor: employee.imageUrl ? undefined : avatarColor }}>
                  {employee.imageUrl ? (
                    <img
                      src={employee.imageUrl.startsWith('http') ? employee.imageUrl : `${window.location.origin}${employee.imageUrl}`}
                      alt={`${employee.name}`}
                      className={`${avatarSize} rounded-full object-cover`}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-white font-bold text-xl">{initials}</span>
                  )}
                </div>
                <div className="w-full">
                  <h3 className="text-sm font-semibold text-foreground truncate">{employee.name}</h3>
                  <p className="text-xs text-muted-foreground truncate">{employee.title || t('common.noTitle')}</p>
                  {isLeaving && <div className="mt-1.5 flex justify-center"><LeavingChip /></div>}
                </div>
              </div>
            )}

            {/* Compact View */}
            {viewMode === 'compact' && (
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <div className={`${avatarSize} rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden`}
                  style={{ backgroundColor: employee.imageUrl ? undefined : avatarColor }}>
                  {employee.imageUrl ? (
                    <img
                      src={employee.imageUrl.startsWith('http') ? employee.imageUrl : `${window.location.origin}${employee.imageUrl}`}
                      alt={employee.name}
                      className={`${avatarSize} rounded-full object-cover`}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-white font-semibold text-xs">{initials}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs font-semibold text-foreground truncate">{employee.name}</h3>
                  <p className="text-[10px] text-muted-foreground truncate">{employee.title || t('common.noTitle')}</p>
                </div>
                {isLeaving && <LeavingChip iconOnly />}
              </div>
            )}

            {/* Detailed View */}
            {viewMode === 'detailed' && (
              <>
                <div className="flex items-center space-x-3 rtl:space-x-reverse mb-3">
                  <div className={`${avatarSize} rounded-full flex items-center justify-center overflow-hidden shadow-md`}
                    style={{ backgroundColor: employee.imageUrl ? undefined : avatarColor }}>
                    {employee.imageUrl ? (
                      <img
                        src={employee.imageUrl.startsWith('http') ? employee.imageUrl : `${window.location.origin}${employee.imageUrl}`}
                        alt={employee.name}
                        className={`${avatarSize} rounded-full object-cover`}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-white font-bold text-lg">{initials}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground truncate">{employee.name}</h3>
                    <p className="text-xs text-muted-foreground truncate">{employee.title || t('common.noTitle')}</p>
                    {isLeaving && <div className="mt-1"><LeavingChip /></div>}
                  </div>
                </div>

                <div className="space-y-1.5 text-xs">
                  <div className={`flex items-center justify-between px-2 py-1 rounded ${deptColors.bg}`}>
                    <span className="text-muted-foreground">{t('Department')}</span>
                    <span className={`font-medium ${deptColors.text}`}>{employee.department || t('common.na')}</span>
                  </div>
                  {employee.directReportIds && employee.directReportIds.length > 0 && (
                    <div className="flex items-center justify-between px-2 py-1">
                      <span className="text-muted-foreground">{t('Direct Reports')}</span>
                      <span className="font-medium text-foreground">{employee.directReportIds.length}</span>
                    </div>
                  )}
                </div>

                {/* Quick Actions on Hover - positioned above expand button area */}
                <div className={`absolute left-0 right-0 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none ${hasDirectReports ? 'bottom-4' : 'bottom-0'}`}>
                  <div className="flex justify-center gap-1 bg-card/95 backdrop-blur rounded-lg py-1.5 px-2 shadow-lg border border-border mx-2 pointer-events-auto">
                    <button
                      onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${employee.email}`; }}
                      className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-muted rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      title={t('Send Email')}
                    >
                      <Icon name="Mail" size={14} className="text-muted-foreground" />
                    </button>
                    {employee.phoneNumber && (
                      <button
                        onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${employee.phoneNumber}`; }}
                        className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-muted rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        title={t('Call')}
                      >
                        <Icon name="Phone" size={14} className="text-muted-foreground" />
                      </button>
                    )}
                    {canOpenProfile(employee.id) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/profile/${employee.id}/personal`); }}
                      className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-muted rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      title={t('View Profile')}
                      data-testid="org-node-view-profile"
                    >
                      <Icon name="User" size={14} className="text-muted-foreground" />
                    </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Expand/Collapse Button */}
            {hasDirectReports && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Find the parent card element to center on
                  const cardElement = (e.target as HTMLElement).closest('.group')?.parentElement;
                  toggleNodeExpansion(employee.id, cardElement as HTMLElement);
                }}
                className={`absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  isExpanded ? 'bg-muted-foreground' : `bg-gradient-to-br ${deptColors.gradient}`
                }`}
              >
                <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={12} color="white" />
              </button>
            )}
          </div>

          {/* Hover Tooltip */}
          {hoveredEmployee?.id === employee.id && viewMode !== 'detailed' && (
            <div className="absolute z-50 top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl w-64 pointer-events-none animate-fade-in">
              <div className="font-semibold mb-1">{employee.name}</div>
              <div className="text-gray-300 mb-2">{employee.title || t('common.noTitle')}</div>
              <div className="space-y-1 text-gray-400">
                <div>{t('Department')}: {employee.department || t('common.na')}</div>
                <div>{t('Email')}: {employee.email}</div>
              </div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-gray-900"></div>
            </div>
          )}

          {/* Curved Connecting Line */}
          {level > 0 && renderConnectorLine(true, true)}
        </div>

        {/* Direct Reports */}
        {hasDirectReports && isExpanded && (
          <div className="mt-10 relative animate-expand-down">
            {/* Drop line from this card down to the siblings' horizontal line. It used to be an SVG
                <path d="M 50% 0 ..."> -- percentages are not valid path data, so the browser logged
                an attribute error per expanded node and drew nothing. */}
            <div className="absolute -top-10 left-1/2 h-4 w-0.5 -translate-x-1/2 bg-border" aria-hidden="true" />

            <div className="flex justify-center gap-8 relative">
              {/* Horizontal line connecting siblings */}
              {employee.directReportIds && employee.directReportIds.length > 1 && (
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 h-0.5 bg-border"
                  style={{ width: `calc(100% - 80px)` }}></div>
              )}

              {employee.directReportIds?.map((reportId) => {
                const reportEmployee = orgData[reportId];
                return reportEmployee ? (
                  <div key={reportId} className="relative">
                    {renderEmployeeCard(reportEmployee, level + 1)}
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render department grouping view
  const renderDepartmentView = () => {
    const employeesByDept = departments.reduce((acc, dept) => {
      acc[dept] = Object.values(orgData).filter(emp => emp.department === dept && applyFilters(emp));
      return acc;
    }, {} as Record<string, OrgDataMap[string][]>);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-8">
        {departments.map(dept => {
          const employees = employeesByDept[dept];
          if (!employees?.length) return null;

          const deptColors = departmentColors[dept] || defaultColors;

          return (
            <div key={dept} className={`rounded-xl border-2 ${deptColors.border} overflow-hidden`}>
              <div className={`px-4 py-3 bg-gradient-to-r ${deptColors.gradient}`}>
                <h3 className="text-white font-semibold">{dept}</h3>
                <p className="text-white/80 text-sm">{employees.length} {t('employees')}</p>
              </div>
              <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
                {employees.map(emp => {
                  const initials = getInitials(emp.name || '');
                  const avatarColor = stringToColor(emp.id);

                  return (
                    <div
                      key={emp.id}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => handleEmployeeClick(emp)}
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: emp.imageUrl ? undefined : avatarColor }}>
                        {emp.imageUrl ? (
                          <img
                            src={emp.imageUrl.startsWith('http') ? emp.imageUrl : `${window.location.origin}${emp.imageUrl}`}
                            alt={emp.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-white font-semibold text-sm">{initials}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground text-sm truncate">{emp.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{emp.title || t('common.noTitle')}</div>
                      </div>
                      {emp.isManager && (
                        <Icon name="Crown" size={14} className="text-amber-500 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4 animate-pulse">
            <Icon name="Network" size={32} color="white" />
          </div>
          <p className="text-muted-foreground">{t('Loading organizational structure...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center bg-card p-8 rounded-2xl shadow-xl max-w-md">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} className="text-destructive" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('Error')}</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('Retry')}
          </button>
        </div>
      </div>
    );
  }

  const breadcrumbPath = selectedEmployee ? getBreadcrumbPath(selectedEmployee.id) : [];

  return (
    <PageLayout
      title={t('Organization Chart')}
      description={t('Interactive hierarchy visualization')}
      icon={Network}
      accentColor="amber"
      maxWidth="1600px"
      stats={[
        { label: t('employees'), value: Object.keys(orgData).length },
      ]}
      statsColumns={2}
    >
    <div className="h-full bg-background flex flex-col overflow-hidden" style={{ minHeight: 'calc(100vh - 250px)' }}>
      {/* Controls Bar */}
      <div className="bg-card border border-border rounded-lg mx-0 mb-4 flex-shrink-0">
        <div className="w-full px-6 py-3 flex-shrink-0">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            {/* Left: Search and Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Icon name="Search" size={16} className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={`${t('Search...')} (${t('press')} /)`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="ps-10 pe-4 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none w-64 text-sm bg-muted focus:bg-card transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute end-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="X" size={14} />
                  </button>
                )}
              </div>

              {/* Department Filter */}
              <select
                value={filters.department}
                onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                className="border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none bg-muted"
              >
                <option value="all">{t('All Departments')}</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>

              {/* Level Filter */}
              <select
                value={filters.managerialLevel}
                onChange={(e) => setFilters(prev => ({ ...prev, managerialLevel: e.target.value }))}
                className="border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none bg-muted"
              >
                <option value="all">{t('All Levels')}</option>
                <option value="executive">{t('Executive')}</option>
                <option value="manager">{t('Manager')}</option>
                <option value="individual">{t('Individual')}</option>
              </select>

              {/* Expand/Collapse Controls */}
              <div className="flex items-center gap-1 border border-border rounded-lg p-1 bg-muted">
                <button
                  onClick={collapseAll}
                  className="p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-card rounded transition-colors text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  title={t('Collapse All')}
                >
                  <Icon name="ChevronsUp" size={16} />
                </button>
                <button
                  onClick={() => expandToLevel(2)}
                  className="px-2 py-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-card rounded transition-colors text-xs font-medium text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  title={t('Expand Level 2')}
                >
                  L2
                </button>
                <button
                  onClick={() => expandToLevel(3)}
                  className="px-2 py-1 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-card rounded transition-colors text-xs font-medium text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  title={t('Expand Level 3')}
                >
                  L3
                </button>
                <button
                  onClick={expandAll}
                  className="p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-card rounded transition-colors text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  title={t('Expand All')}
                >
                  <Icon name="ChevronsDown" size={16} />
                </button>
              </div>
            </div>

            {/* Right: View Mode, Zoom, and Actions */}
            <div className="flex items-center gap-2">
              {/* Layout Mode Toggle */}
              <div className="flex items-center border border-border rounded-lg p-1 bg-muted">
                <button
                  onClick={() => setLayoutMode('hierarchy')}
                  className={`p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center rounded transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${layoutMode === 'hierarchy' ? 'bg-card shadow-sm text-primary' : 'text-muted-foreground hover:bg-card'}`}
                  title={t('Hierarchy View')}
                >
                  <Icon name="GitBranch" size={16} />
                </button>
                <button
                  onClick={() => setLayoutMode('department')}
                  className={`p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center rounded transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${layoutMode === 'department' ? 'bg-card shadow-sm text-primary' : 'text-muted-foreground hover:bg-card'}`}
                  title={t('Department View')}
                >
                  <Icon name="LayoutGrid" size={16} />
                </button>
              </div>

              {/* View Mode */}
              {layoutMode === 'hierarchy' && (
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as 'photo' | 'compact' | 'detailed')}
                  className="border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus:outline-none bg-muted"
                >
                  <option value="photo">{t('Photo')}</option>
                  <option value="compact">{t('Compact')}</option>
                  <option value="detailed">{t('Detailed')}</option>
                </select>
              )}

              {/* Zoom Controls */}
              {layoutMode === 'hierarchy' && (
                <div className="flex items-center gap-1 border border-border rounded-lg p-1 bg-muted">
                  <button
                    onClick={() => handleZoom('out')}
                    className="p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-card rounded transition-colors disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    disabled={zoomLevel <= 0.3}
                  >
                    <Icon name="ZoomOut" size={16} />
                  </button>
                  <span className="text-xs text-muted-foreground min-w-[40px] text-center font-medium">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <button
                    onClick={() => handleZoom('in')}
                    className="p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-card rounded transition-colors disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    disabled={zoomLevel >= 3}
                  >
                    <Icon name="ZoomIn" size={16} />
                  </button>
                  <div className="w-px h-5 bg-border mx-1"></div>
                  <button onClick={() => handleZoom('reset')} className="p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-card rounded transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" title={t('Reset Zoom')}>
                    <Icon name="RotateCcw" size={16} />
                  </button>
                  <button onClick={() => handleZoom('fit')} className="p-1.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center hover:bg-card rounded transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" title={t('Fit to View')}>
                    <Icon name="Maximize2" size={16} />
                  </button>
                </div>
              )}

              {/* Export */}
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-3 py-2 bg-muted text-foreground rounded-lg hover:bg-muted transition-colors text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Icon name="Download" size={16} />
                <span className="hidden sm:inline">{t('Export')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb Trail */}
      {selectedEmployee && breadcrumbPath.length > 1 && (
        <div className="bg-card border-b border-border px-6 py-2 flex-shrink-0">
          <div className="flex items-center gap-2 text-sm overflow-x-auto">
            {breadcrumbPath.map((item, index) => (
              <React.Fragment key={item.id}>
                {index > 0 && <Icon name="ChevronRight" size={14} className="text-muted-foreground flex-shrink-0 rtl:rotate-180" />}
                <button
                  onClick={() => {
                    const emp = orgData[item.id];
                    if (emp) {
                      setSelectedEmployee(emp);
                      setKeyboardFocusId(item.id);
                    }
                  }}
                  className={`px-2 py-1 rounded transition-colors flex-shrink-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    item.id === selectedEmployee.id
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {item.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden min-h-0 max-h-full">
        {/* Chart Container */}
        <div
          ref={chartContainerRef}
          className={`flex-1 overflow-hidden ${layoutMode === 'hierarchy' ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : 'overflow-auto'}`}
          onMouseDown={layoutMode === 'hierarchy' ? handleMouseDown : undefined}
          onMouseMove={layoutMode === 'hierarchy' ? handleMouseMove : undefined}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {layoutMode === 'hierarchy' ? (
            <div
              ref={chartRef}
              className="inline-block min-w-full p-8"
              style={{
                transform: `scale(${zoomLevel}) translate(${panStateRef.current.offsetX / zoomLevel}px, ${panStateRef.current.offsetY / zoomLevel}px)`,
                transformOrigin: 'top center',
                willChange: isPanning ? 'transform' : 'auto',
                transition: isPanning ? 'none' : 'transform 0.2s ease-out'
              }}
            >
              {rootEmployeeIds.length > 0 ? (
                <div className="flex flex-wrap justify-center gap-12">
                  {rootEmployeeIds.map(rootId => {
                    const rootEmp = orgData[rootId];
                    if (!rootEmp) return null;
                    return (
                      <div key={rootId} className="flex flex-col items-center" data-org-root={rootId}>
                        {renderEmployeeCard(rootEmp)}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">{t('No organizational data')}</h3>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-auto h-full">
              {renderDepartmentView()}
            </div>
          )}
        </div>

        {/* Side Panel for Employee Details */}
        {selectedEmployee && (
          <div
            className="bg-card border-s border-border shadow-2xl overflow-y-auto overflow-x-hidden animate-slide-in-right flex-shrink-0 h-full w-full sm:w-[380px]"
            style={{ minWidth: '0' }}>
            <div className="p-6 w-full">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">{t('Employee Details')}</h2>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {/* Profile Content */}
              <div className="space-y-6">
                {/* Photo and Basic Info */}
                <div className="text-center pb-6 border-b border-border">
                  <div className="w-32 h-32 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden shadow-lg ring-4 ring-white"
                    style={{ backgroundColor: selectedEmployee.imageUrl ? '#E5E7EB' : stringToColor(selectedEmployee.id) }}>
                    {selectedEmployee.imageUrl ? (
                      <img
                        src={selectedEmployee.imageUrl.startsWith('http') ? selectedEmployee.imageUrl : `${window.location.origin}${selectedEmployee.imageUrl}`}
                        alt={selectedEmployee.name}
                        className="w-32 h-32 rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-white font-bold text-4xl">{getInitials(selectedEmployee.name || '')}</span>
                    )}
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-1">{selectedEmployee.name}</h3>
                  <p className="text-muted-foreground text-lg mb-4">{selectedEmployee.title || t('common.noTitle')}</p>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {selectedEmployee.isManager && (
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-400 to-amber-500 text-white rounded-full text-sm font-medium shadow-sm">
                        <Icon name="Crown" size={14} />
                        <span>{t('Manager')}</span>
                      </div>
                    )}
                    <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                      (departmentColors[selectedEmployee.department || ''] || defaultColors).bg
                    } ${(departmentColors[selectedEmployee.department || ''] || defaultColors).text}`}>
                      {selectedEmployee.department || t('orgChart.noDepartment')}
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => window.location.href = `mailto:${selectedEmployee.email}`}
                    className="flex flex-col items-center gap-2 p-4 bg-muted rounded-xl hover:bg-muted transition-colors min-h-[44px] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <Icon name="Mail" size={24} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t('Email')}</span>
                  </button>
                  {selectedEmployee.phoneNumber && (
                    <button
                      onClick={() => window.location.href = `tel:${selectedEmployee.phoneNumber}`}
                      className="flex flex-col items-center gap-2 p-4 bg-muted rounded-xl hover:bg-muted transition-colors min-h-[44px] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <Icon name="Phone" size={24} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">{t('Call')}</span>
                    </button>
                  )}
                  {canOpenProfile(selectedEmployee.id) && (
                  <button
                    onClick={() => navigate(`/profile/${selectedEmployee.id}/personal`)}
                    className="flex flex-col items-center gap-2 p-4 bg-muted rounded-xl hover:bg-muted transition-colors min-h-[44px] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="org-panel-profile"
                  >
                    <Icon name="ExternalLink" size={24} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{t('Profile')}</span>
                  </button>
                  )}
                </div>

                {/* Contact Information */}
                <div className="bg-muted rounded-xl p-4">
                  <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Contact" size={18} className="text-muted-foreground" />
                    {t('Contact Information')}
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-card rounded-lg flex items-center justify-center shadow-sm">
                        <Icon name="Mail" size={16} className="text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-muted-foreground">{t('Email')}</div>
                        <div className="text-sm text-foreground truncate">{selectedEmployee.email}</div>
                      </div>
                    </div>
                    {selectedEmployee.phoneNumber && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-card rounded-lg flex items-center justify-center shadow-sm">
                          <Icon name="Phone" size={16} className="text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">{t('Phone')}</div>
                          <div className="text-sm text-foreground">{selectedEmployee.phoneNumber}</div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-card rounded-lg flex items-center justify-center shadow-sm">
                        <Icon name="Building2" size={16} className="text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-muted-foreground">{t('Department')}</div>
                        <div className="text-sm text-foreground">{selectedEmployee.department || t('common.na')}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reporting Structure */}
                <div className="bg-muted rounded-xl p-4">
                  <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="GitBranch" size={18} className="text-muted-foreground" />
                    {t('Reporting Structure')}
                  </h4>
                  <div className="space-y-3">
                    {selectedEmployee.managerId && orgData[selectedEmployee.managerId] && (
                      <div
                        className="flex items-center gap-3 p-3 bg-card rounded-lg cursor-pointer hover:shadow-md transition-all"
                        onClick={() => setSelectedEmployee(orgData[selectedEmployee.managerId!])}
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: stringToColor(selectedEmployee.managerId) }}>
                          {orgData[selectedEmployee.managerId].imageUrl ? (
                            <img
                              src={orgData[selectedEmployee.managerId].imageUrl!.startsWith('http')
                                ? orgData[selectedEmployee.managerId].imageUrl!
                                : `${window.location.origin}${orgData[selectedEmployee.managerId].imageUrl}`}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-semibold text-sm">
                              {getInitials(orgData[selectedEmployee.managerId].name || '')}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">{t('Reports to')}</div>
                          <div className="text-sm font-medium text-foreground truncate">
                            {orgData[selectedEmployee.managerId].name}
                          </div>
                        </div>
                        <Icon name="ChevronRight" size={16} className="text-muted-foreground rtl:rotate-180" />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-card rounded-lg text-center">
                        <div className="text-2xl font-bold text-foreground">{selectedEmployee.directReportIds?.length || 0}</div>
                        <div className="text-xs text-muted-foreground">{t('Direct Reports')}</div>
                      </div>
                      <div className="p-3 bg-card rounded-lg text-center">
                        <div className="text-2xl font-bold text-foreground">{selectedEmployee.employeeCount || 0}</div>
                        <div className="text-xs text-muted-foreground">{t('Total Team')}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Direct Reports List */}
                {selectedEmployee.directReportIds && selectedEmployee.directReportIds.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Users" size={18} className="text-muted-foreground" />
                      {t('Direct Reports')} ({selectedEmployee.directReportIds.length})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {selectedEmployee.directReportIds.map(reportId => {
                        const report = orgData[reportId];
                        return report ? (
                          <div
                            key={reportId}
                            className="flex items-center gap-3 p-3 bg-muted rounded-lg hover:bg-muted transition-colors cursor-pointer"
                            onClick={() => setSelectedEmployee(report)}
                          >
                            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: report.imageUrl ? '#E5E7EB' : stringToColor(reportId) }}>
                              {report.imageUrl ? (
                                <img
                                  src={report.imageUrl.startsWith('http') ? report.imageUrl : `${window.location.origin}${report.imageUrl}`}
                                  alt={report.name}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <span className="text-white font-semibold text-sm">{getInitials(report.name || '')}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-foreground text-sm truncate">{report.name}</div>
                              <div className="text-xs text-muted-foreground truncate">{report.title || t('common.noTitle')}</div>
                            </div>
                            {report.isManager && <Icon name="Crown" size={14} className="text-amber-500 flex-shrink-0" />}
                            <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0 rtl:rotate-180" />
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* View Full Profile Button — CO-833: only for profiles the viewer may open (see canOpenProfile) */}
                {canOpenProfile(selectedEmployee.id) && (
                <button
                  onClick={() => navigate(`/profile/${selectedEmployee.id}/personal`)}
                  className="w-full bg-gradient-to-r from-primary to-primary/90 text-white py-3 px-4 rounded-xl hover:shadow-lg transition-all font-medium flex items-center justify-center gap-2"
                  data-testid="org-panel-view-full-profile"
                >
                  <Icon name="ExternalLink" size={18} />
                  {t('View Full Profile')}
                </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legend - Floating */}
      <button
        onClick={() => setShowLegend(!showLegend)}
        className="fixed bottom-6 start-18 bg-card shadow-lg rounded-full px-4 py-2 flex items-center gap-2 text-sm font-medium text-foreground hover:shadow-xl transition-all z-20 min-h-[44px] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <Icon name="Info" size={16} />
        {t('Legend')}
      </button>

      {showLegend && (
        <div className="fixed bottom-20 start-18 bg-card rounded-xl shadow-2xl p-4 w-80 z-20 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-foreground">{t('Legend')}</h3>
            <button onClick={() => setShowLegend(false)} className="p-1 hover:bg-muted rounded focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
              <Icon name="X" size={16} />
            </button>
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <div className="font-medium text-foreground mb-2">{t('Departments')}</div>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(departmentColors).slice(0, 6).map(([dept, colors]) => (
                  <div key={dept} className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded ${colors.bg} border ${colors.border}`}></div>
                    <span className="text-muted-foreground text-xs">{dept}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="font-medium text-foreground mb-2">{t('Icons')}</div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
                    <Icon name="Crown" size={10} color="white" />
                  </div>
                  <span className="text-muted-foreground text-xs">{t('Manager')}</span>
                </div>
              </div>
            </div>
            <div>
              <div className="font-medium text-foreground mb-2">{t('Keyboard Shortcuts')}</div>
              <div className="space-y-1 text-xs text-muted-foreground">
                <div><kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">/</kbd> {t('Focus search')}</div>
                <div><kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">↑↓←→</kbd> {t('Navigate')}</div>
                <div><kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Enter</kbd> {t('Expand/Select')}</div>
                <div><kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Esc</kbd> {t('Close panel')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes expand-down {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes draw-line {
          from { stroke-dashoffset: 100; }
          to { stroke-dashoffset: 0; }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
        .animate-expand-down { animation: expand-down 0.3s ease-out; }
        .animate-draw-line {
          stroke-dasharray: 100;
          animation: draw-line 0.5s ease-out forwards;
        }
      `}</style>
    </div>
    </PageLayout>
  );
};

export default OrganizationalStructure;
